import React, { useEffect, useState, useCallback, useMemo } from "react";
import style from "../beetle-plague/beetle-lague.module.css";
import BeetleImage from "../../../../images/beetle-colored.png";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ParkOutlinedIcon from "@mui/icons-material/ParkOutlined";
import logger from "../../../../utils/logger";
import BracellRowExtras from "./BracellRowExtras";
import BracellLevelPanel from "./BracellLevelPanel";
import {
  diagnoseBracellBeetle,
  getPlotAgeInYears,
} from "../../config/bracellRegistryProfile";

function createRow(index, plantCount) {
  const pc = Math.max(1, plantCount);
  return {
    planta: (index % pc) + 1,
    parcela: Math.floor(index / pc) + 1,
    percentualDesfolha: 0,
    desfolha_incidencia_percent: "0",
    dano_severidade: "Sem Dano",
  };
}

function groupByParcela(samples, plantCount) {
  const pc = Math.max(1, plantCount);
  const groups = [];
  for (let i = 0; i < samples.length; i += pc) {
    groups.push(samples.slice(i, i + pc));
  }
  return groups;
}

function BracellBeetlePlague({ plague, infestation, data, plantCount, plot, parcelas: parcelasProp }) {
  const [samples, setSamples] = useState([]);
  const isValidPlague =
    plague && typeof plague === "object" && plague.name && plague.scientific;

  const numParcelas = Math.max(1, Number(parcelasProp) || 2);
  const numPlantCount = Math.max(1, Number(plantCount) || 5);

  useEffect(() => {
    const total = numParcelas * numPlantCount;
    setSamples(Array.from({ length: total }, (_, i) => createRow(i, numPlantCount)));
  }, [numParcelas, numPlantCount]);

  const patchRow = useCallback((index, patch) => {
    setSamples((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  }, []);

  const setInsetos = (index, value) => {
    const v = parseInt(value, 10);
    patchRow(index, {
      percentualDesfolha: Number.isFinite(v) && v >= 0 ? v : 0,
    });
  };

  const bump = (index, delta) => {
    setSamples((prev) => {
      const next = [...prev];
      const cur = parseInt(next[index].percentualDesfolha, 10) || 0;
      next[index] = {
        ...next[index],
        percentualDesfolha: Math.max(0, cur + delta),
      };
      return next;
    });
  };

  useEffect(() => {
    if (!data || !isValidPlague) return;
    data(samples);
  }, [samples, data, isValidPlague]);

  const ageYears = getPlotAgeInYears(plot);

  const { lines, displayText, levelKey, diagnostic, prob } = useMemo(() => {
    const groups = groupByParcela(samples, numPlantCount);
    const parcelaMeans = groups.map((group) => {
      const sum = group.reduce((acc, s) => acc + (parseInt(s.percentualDesfolha, 10) || 0), 0);
      return group.length > 0 ? sum / group.length : 0;
    });
    const mean = parcelaMeans.length > 0
      ? parcelaMeans.reduce((a, b) => a + b, 0) / parcelaMeans.length
      : 0;

    const d = diagnoseBracellBeetle(mean, ageYears);
    const disp = d.levelKey === "none" ? "-" : d.diagnostic;

    const ls = [];
    if (numParcelas > 1) {
      parcelaMeans.forEach((m, p) => {
        ls.push({ label: `Parcela ${p + 1} — Média de insetos`, value: m.toFixed(2) });
      });
    }
    ls.push({
      label: numParcelas > 1 ? "Média geral de insetos por planta" : "Média de insetos por planta",
      value: mean.toFixed(2),
    });
    ls.push({
      label: "Idade talhão (anos)",
      value:
        ageYears != null && Number.isFinite(ageYears)
          ? ageYears.toFixed(2)
          : "— (adulta)",
    });
    return {
      lines: ls,
      displayText: disp,
      levelKey: d.levelKey,
      diagnostic: d.diagnostic,
      prob: d.prob,
    };
  }, [samples, numPlantCount, numParcelas, ageYears]);

  useEffect(() => {
    if (!isValidPlague) return;
    infestation({ diagnostic, prob });
  }, [diagnostic, prob, infestation, isValidPlague]);

  if (!isValidPlague) {
    logger.warn("Objeto de praga inválido:", plague);
    return null;
  }

  return (
    <div className={style["beetle-plague"]}>
      <div className={style.card}>
        <div className={style["sample-plague"]}>
          <img src={BeetleImage} alt="" />
          <div className={style["name-sample-plague"]}>
            <div>{plague.name}</div>
            <div>{plague.scientific}</div>
          </div>
        </div>

        <div className={style.samples}>
          {samples.map((sample, index) => {
            const parcelaStart = index % numPlantCount === 0;
            const parcelaNum = Math.floor(index / numPlantCount) + 1;
            return (
              <React.Fragment key={index}>
                {numParcelas > 1 && parcelaStart && (
                  <div style={{ fontWeight: "600", fontSize: "0.85rem", padding: "10px 0 4px", color: "#40a8c4" }}>
                    Parcela {parcelaNum}
                  </div>
                )}
                <div className={style["collected-sample-title"]}>
                  <div className={style["collected-sample-plant"]}>
                    <div className={style["collected-sample-plant-number"]}>
                      {sample.planta}° <ParkOutlinedIcon style={{ width: "0.8rem" }} />
                    </div>
                  </div>
                  <div className={style["information-unsheet"]}>
                    <div className={style.name}>Nº de insetos (planta)</div>
                    <div className={style["contianer-input"]}>
                      <RemoveIcon
                        onClick={() => bump(index, -1)}
                        className={style.icon}
                      />
                      <input
                        value={sample.percentualDesfolha}
                        className={style.input}
                        type="number"
                        min={0}
                        onChange={(e) => setInsetos(index, e.target.value)}
                      />
                      <AddIcon
                        onClick={() => bump(index, 1)}
                        className={style.icon}
                      />
                    </div>
                  </div>
                  <BracellRowExtras
                    desfolha_incidencia_percent={sample.desfolha_incidencia_percent}
                    dano_severidade={sample.dano_severidade}
                    onPatch={(p) => patchRow(index, p)}
                  />
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <BracellLevelPanel
        lines={lines}
        displayText={displayText}
        levelKey={levelKey}
      />
    </div>
  );
}

export default BracellBeetlePlague;
